import { useEffect, useMemo, useState } from 'react';
import { RecipeClient } from './recipeClient';
import { useRecipeStore } from '@/stores/recipeStore';
import { PREDEFINED_TAG_GROUPS } from '@/constants/tags';
import type { CreateRecipeRequest, UpdateRecipeRequest, Recipe } from '@/types/api-models';
import type { RecipeTagGroup } from '@/lib/recipeClient';

export interface RecipeQueryParams {
  /** Return only the recipe with this ID. Short-circuits all other filters. */
  id?: string;
  search?: string;
  /** Simple tag filter — all tags must match (AND). Use tagsByGroup for OR-within-group / AND-across-groups. */
  tags?: string[];
  /**
   * Group-aware tag filter: OR within each group, AND across groups.
   * e.g. { Cuisine: ['Italian', 'Mexican'], Diet: ['Vegetarian'] }
   * matches recipes that are (Italian OR Mexican) AND Vegetarian.
   */
  tagsByGroup?: Record<string, string[]>;
  minRating?: number;
  /** Sort order applied after filtering. 'recent' = newest createdAt first. */
  sort?: 'recent';
  /** Return only the first N results after filtering and sorting. */
  limit?: number;
}

/**
 * Pure in-memory filter/sort/limit on a recipe array.
 *
 * Exported as a standalone function (not on RecipeService) to make it
 * clear this is a pure utility — it does not touch the store or the network.
 * Callers supply the data they already have; the function just transforms it.
 */
export function filterRecipes(recipes: Recipe[], params?: RecipeQueryParams): Recipe[] {
  if (params?.id != null) {
    const match = recipes.find((r) => r.id === params.id);
    return match ? [match] : [];
  }

  let output = recipes;

  const search = params?.search?.trim();
  if (search) {
    const words = search.toLowerCase().split(/\s+/);
    output = output.filter((r) => {
      const title = r.title.toLowerCase();
      return words.every((word) => title.includes(word));
    });
  }

  if (params?.tagsByGroup && Object.keys(params.tagsByGroup).length > 0) {
    output = output.filter((r) =>
      Object.values(params.tagsByGroup!).every((groupTags) =>
        groupTags.some((t) => r.tags?.includes(t))
      )
    );
  } else if (params?.tags?.length) {
    output = output.filter((r) => params.tags!.every((t) => r.tags?.includes(t)));
  }

  if (params?.minRating != null) {
    output = output.filter((r) => (r.rating ?? 0) >= params.minRating!);
  }

  if (params?.sort === 'recent') {
    output = [...output].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }

  if (params?.limit != null) {
    output = output.slice(0, params.limit);
  }

  return output;
}

/**
 * Data management layer for recipes.
 *
 * - Reads:    useRecipes() provides a reactive subscription for components.
 *             ensureLoaded() triggers the store fetch if not yet loaded.
 *             findById() returns a one-shot value from the current store state.
 * - Writes:   create/update/delete call the API and keep the store in sync.
 */
export const RecipeService = {
  /**
   * Fire-and-forget: triggers a full fetch if the store hasn't loaded yet.
   * Safe to call from useEffect — idempotent if already loading or loaded.
   */
  ensureLoaded(): void {
    const { status } = useRecipeStore.getState();
    if (status === 'idle') void RecipeService.load();
  },

  async load(): Promise<void> {
    const store = useRecipeStore.getState();
    if (store.status === 'loading') return;
    store.setStatus('loading');
    try {
      const PAGE_SIZE = 50;
      const collected: Recipe[] = [];
      let page = 1;
      let hasNextPage = true;
      while (hasNextPage) {
        const res = await RecipeClient.getRecipes({ page, limit: PAGE_SIZE });
        collected.push(...res.recipes);
        store.setRecipes([...collected]);
        hasNextPage = res.pagination.hasNextPage;
        page++;
      }
      store.setStatus('loaded');
    } catch {
      store.setStatus('error');
    }
  },

  async loadDelta(since: string): Promise<void> {
    const store = useRecipeStore.getState();
    if (store.status === 'loading') return;
    store.setStatus('loading');
    try {
      const changed = await RecipeClient.getRecipesSince(since);
      const current = useRecipeStore.getState().recipes;
      let list = [...current];
      for (const recipe of changed) {
        const idx = list.findIndex((r) => r.id === recipe.id);
        if (idx >= 0) { list[idx] = recipe; } else { list = [recipe, ...list]; }
      }
      store.setRecipes(list);
      store.setStatus('loaded');
    } catch {
      store.setStatus('error');
    }
  },

  async reload(): Promise<void> {
    return RecipeService.load();
  },

  /**
   * Fetches a single recipe by ID and upserts it into the store.
   * Use instead of reload() when only one recipe changed — avoids a full
   * paginated fetch for a single-item update (e.g. AI chef create/update).
   */
  async loadOne(recipeId: string): Promise<void> {
    const recipe = await RecipeClient.getRecipe(recipeId);
    useRecipeStore.getState().upsert(recipe);
  },

  clear(): void {
    useRecipeStore.getState().clear();
  },

  /**
   * Clears the store and triggers a full reload from the API.
   * Use for "Refresh app data" / hard-reset scenarios.
   */
  async reset(): Promise<void> {
    RecipeService.clear();
    return RecipeService.load();
  },

  findById(id: string): Recipe | undefined {
    return useRecipeStore.getState().recipes.find((r) => r.id === id);
  },

  /**
   * React hook: reactive subscription to the recipe store.
   *
   * Re-renders callers whenever the store changes. All filter/sort/limit
   * logic is delegated to filter() — this method only owns the React-specific
   * subscription and memoisation.
   *
   * The store is intentionally kept as an internal detail of this file;
   * nothing outside the repository should import from recipeStore directly.
   */
  useRecipes(params?: RecipeQueryParams) {
    useEffect(() => {
      RecipeService.ensureLoaded();
    }, []);

    const { recipes, status } = useRecipeStore();
    const hasData = recipes.length > 0 || status === 'loaded';

    const result = useMemo(
      () => (hasData ? filterRecipes(recipes, params) : []),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [recipes, hasData, params?.id, params?.search, params?.tags, JSON.stringify(params?.tagsByGroup), params?.minRating, params?.sort, params?.limit],
    );

    return {
      recipes: result,
      recipe: result[0] as Recipe | undefined,
      isLoading: !hasData && (status === 'loading' || status === 'idle'),
      isRefreshing: hasData && status === 'loading',
      isError: status === 'error',
    };
  },

  /**
   * React hook: derives tag group data from the in-memory recipe store.
   *
   * - `tagGroups`    — groups that have ≥1 recipe in the user's collection,
   *                    optionally filtered by `exclude`. Use for filter chips.
   * - `allTagGroups` — every predefined group, even empty ones, never excluded.
   *                    Use for recipe editing (you want to browse all categories).
   *
   * No network requests — tags stay in sync with the recipe store automatically.
   */
  useTagGroups(options?: { exclude?: readonly string[] }): {
    tagGroups: RecipeTagGroup[];
    allTagGroups: RecipeTagGroup[];
    isLoading: boolean;
  } {
    useEffect(() => {
      RecipeService.ensureLoaded();
    }, []);

    const recipes = useRecipeStore((state) => state.recipes);
    const status = useRecipeStore((state) => state.status);
    const exclude = options?.exclude;

    const { tagGroups, allTagGroups } = useMemo(() => {
      const usedTags = new Set(recipes.flatMap((r) => r.tags ?? []));

      const filtered = PREDEFINED_TAG_GROUPS.map((group) => ({
        name: group.name,
        tags: group.tags.filter((t) => usedTags.has(t)),
      }))
        .filter((group) => group.tags.length > 0)
        .filter((group) => !exclude?.includes(group.name));

      const all = PREDEFINED_TAG_GROUPS.map((group) => ({
        name: group.name,
        tags: [...group.tags],
      }));

      return { tagGroups: filtered, allTagGroups: all };
    }, [recipes, exclude]);

    return {
      tagGroups,
      allTagGroups,
      isLoading: status === 'loading' || status === 'idle',
    };
  },

  // ---- Mutations ----

  async create(data: CreateRecipeRequest): Promise<Recipe> {
    const recipe = await RecipeClient.createRecipe(data);
    useRecipeStore.getState().upsert(recipe);
    return recipe;
  },

  async update(id: string, data: UpdateRecipeRequest): Promise<Recipe> {
    const recipe = await RecipeClient.updateRecipe(id, data);
    useRecipeStore.getState().upsert(recipe);
    return recipe;
  },

  async delete(id: string): Promise<void> {
    await RecipeClient.deleteRecipe(id);
    useRecipeStore.getState().remove(id);
  },

  async updateRating(id: string, rating: number): Promise<Recipe> {
    const recipe = await RecipeClient.updateRecipeRating(id, rating);
    useRecipeStore.getState().upsert(recipe);
    return recipe;
  },

  async uploadImage(recipeId: string, file: File): Promise<Recipe> {
    const updated = await RecipeClient.uploadRecipeImage(recipeId, file);
    useRecipeStore.getState().upsert(updated);
    return updated;
  },

  async copyShared(token: string): Promise<Recipe> {
    const response = await RecipeClient.copySharedRecipe(token);
    useRecipeStore.getState().upsert(response.recipe);
    return response.recipe;
  },
};

/**
 * Convenience named export so components can write:
 *   import { useRecipes } from '@/lib/recipeService'
 * rather than importing RecipeService and calling the method.
 */
export function useRecipes(params?: RecipeQueryParams) {
  return RecipeService.useRecipes(params);
}

/**
 * Convenience named export for tag group derivation.
 *   import { useTagGroups } from '@/lib/recipeService'
 */
export function useTagGroups(options?: { exclude?: readonly string[] }) {
  return RecipeService.useTagGroups(options);
}

/**
 * Reactive selector for a set of recipes by ID list.
 *
 * Returns the matching Recipe objects in the order of `ids`, skipping any
 * IDs not yet in the store. Re-renders only when the store changes or the
 * ID list changes.
 *
 * `isLoading` is true while the store has not yet completed its initial fetch.
 * Callers should show a skeleton rather than an empty state while loading.
 */
export function useRecipesByIds(ids: string[]): { recipes: Recipe[]; isLoading: boolean } {
  // Call synchronously so the fetch starts on first render, not after the
  // first useEffect flush — prevents an empty initial render when navigating
  // directly to a collection page before the recipe store is populated.
  RecipeService.ensureLoaded();

  const recipes = useRecipeStore((state) => state.recipes);
  const status = useRecipeStore((state) => state.status);
  // idsKey stabilises the dep when the caller passes a new array literal each render
  const idsKey = ids.join(',');
  const matched = useMemo(
    () => {
      const map = new Map(recipes.map((r) => [r.id, r]));
      return ids.flatMap((id) => { const r = map.get(id); return r ? [r] : []; });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recipes, idsKey],
  );
  return {
    recipes: matched,
    isLoading: status === 'idle' || (status === 'loading' && recipes.length === 0),
  };
}

/**
 * Reactive selector for a single recipe by ID.
 *
 * More efficient than useRecipes({ id }) — subscribes only to the specific
 * recipe entry rather than the full array, so unrelated recipe updates don't
 * trigger a re-render.
 */
export function useRecipeById(id: string | undefined) {
  const recipe = useRecipeStore((state) =>
    id ? state.recipes.find((r) => r.id === id) : undefined,
  );
  const status = useRecipeStore((state) => state.status);

  const [missing, setMissing] = useState(false);

  useEffect(() => {
    // Kick off the full collection load too, so lists/tags/search populate.
    RecipeService.ensureLoaded();
  }, []);

  useEffect(() => {
    if (!id || recipe) return;
    let cancelled = false;
    setMissing(false);
    RecipeService.loadOne(id).catch(() => {
      if (!cancelled) setMissing(true);
    });
    return () => { cancelled = true; };
  }, [id, recipe]);

  return {
    recipe,
    // Missing from the store but not yet proven absent ⇒ still loading. Once
    // loadOne upserts it, `recipe` is defined and this flips to false; on a
    // genuine 404 it flips to isError instead.
    isLoading: !!id && !recipe && !missing,
    isError: status === 'error' || (!recipe && missing),
  };
}
